import { useState } from 'react';
import { SEO } from '../../utils/seo';

type InspectionType = 'construction' | 'bridge' | 'tower' | 'utility';
type BridgeSize = 'small' | 'medium' | 'large';
type InspectionDepth = 'visual' | 'thermal' | 'diagnostic';

export default function InfrastructurePricingPage() {
  const [inspectionType, setInspectionType] = useState<InspectionType>('construction');
  const [constructionValue, setConstructionValue] = useState(5000000);
  const [bridgeSize, setBridgeSize] = useState<BridgeSize>('medium');
  const [towerCount, setTowerCount] = useState(1);
  const [utilityMiles, setUtilityMiles] = useState(10);
  const [inspectionDepth, setInspectionDepth] = useState<InspectionDepth>('visual');
  const [priorityTurnaround, setPriorityTurnaround] = useState(false);
  const [engineerReport, setEngineerReport] = useState(false);

  const calculatePrice = () => {
    let basePrice = 0;
    let multiplier = 1.0;
    let projectSize = '';

    // Calculate base price and multiplier based on inspection type
    switch (inspectionType) {
      case 'construction':
        const millions = constructionValue / 1000000;
        basePrice = millions * 1000;
        projectSize = `$${(constructionValue / 1000000).toFixed(1)}M project`;
        
        // Small project scaling: 1.5x at $250k, 1.0x at $10M
        if (constructionValue < 10000000) {
          const normalizedSize = (constructionValue - 250000) / (10000000 - 250000);
          multiplier = 1.5 - (normalizedSize * 0.5);
        }
        break;

      case 'bridge':
        if (bridgeSize === 'small') {
          basePrice = 2500;
          multiplier = 1.5;
        } else if (bridgeSize === 'medium') {
          basePrice = 5000;
          multiplier = 1.0;
        } else {
          basePrice = 8000;
          multiplier = 1.0;
        }
        projectSize = `${bridgeSize.charAt(0).toUpperCase() + bridgeSize.slice(1)} bridge`;
        break;

      case 'tower':
        basePrice = 2500 * towerCount;
        projectSize = `${towerCount} structure${towerCount > 1 ? 's' : ''}`;
        
        // Small project scaling: 1.5x at 1 structure, 1.0x at 1+ structures
        if (towerCount === 1) {
          multiplier = 1.5;
        }
        break;

      case 'utility':
        basePrice = 250 * utilityMiles;
        projectSize = `${utilityMiles} mile${utilityMiles > 1 ? 's' : ''}`;
        
        // Small project scaling: 1.5x at 0.5 miles, 1.0x at 20 miles
        if (utilityMiles < 20) {
          const normalizedSize = (utilityMiles - 0.5) / (20 - 0.5);
          multiplier = 1.5 - (normalizedSize * 0.5);
        }
        break;
    }

    // Apply multiplier
    let adjustedPrice = basePrice * multiplier;

    // Apply inspection depth modifier
    let depthMultiplier = 1.0;
    if (inspectionDepth === 'thermal') {
      depthMultiplier = 1.3;
    } else if (inspectionDepth === 'diagnostic') {
      depthMultiplier = 1.6;
    }
    adjustedPrice *= depthMultiplier;

    // Apply add-ons
    if (priorityTurnaround) {
      adjustedPrice *= 1.15;
    }
    if (engineerReport) {
      adjustedPrice *= 1.2;
    }

    // Calculate range (±15%)
    const lowPrice = Math.round(adjustedPrice * 0.85);
    const highPrice = Math.round(adjustedPrice * 1.15);

    return {
      lowPrice,
      highPrice,
      projectSize,
      multiplier: multiplier.toFixed(2)
    };
  };

  const result = calculatePrice();

  const getInspectionTypeLabel = () => {
    switch (inspectionType) {
      case 'construction': return 'Construction Site';
      case 'bridge': return 'Bridge / Structural';
      case 'tower': return 'Water Tower / Vertical Asset';
      case 'utility': return 'Utility / Linear Infrastructure';
    }
  };

  const getInspectionDepthLabel = () => {
    switch (inspectionDepth) {
      case 'visual': return 'Visual Only';
      case 'thermal': return 'Visual + Thermal';
      case 'diagnostic': return 'Full Diagnostic Package';
    }
  };

  return (
    <>
      <SEO
        title="Infrastructure Drone Inspection Pricing | Instant Estimates"
        description="Estimate pricing for drone inspections of bridges, utilities, construction sites, and infrastructure. Scalable pricing and fast quotes."
        keywords="infrastructure inspection pricing, drone inspection cost, bridge inspection pricing, utility inspection rates, construction inspection pricing"
        canonical="https://yourdomain.com/infrastructure-pricing"
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white py-24">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://public.readdy.ai/ai/img_res/bfc20bd508a300e9f5daa9d78a364648.jpg"
              alt="Infrastructure inspection"
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-5xl font-bold mb-6">
              Clear Pricing for Infrastructure Inspections
            </h1>
            <p className="text-xl text-stone-200 mb-8 max-w-3xl mx-auto">
              Use the calculator to estimate cost by asset type and size. Final pricing reflects access, environment, and reporting needs.
            </p>
            <button
              onClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Estimate Your Price
            </button>
          </div>
        </section>

        {/* Calculator Section */}
        <section id="calculator" className="py-20 bg-stone-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-stone-900 mb-4">
                Infrastructure Inspection Estimator
              </h2>
              <p className="text-lg text-stone-600 max-w-3xl mx-auto">
                Select your inspection type, enter project details, and see an estimated price range. Pricing scales fairly with project size and complexity.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Calculator Inputs */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-stone-900 mb-6">Project Details</h3>

                {/* Inspection Type */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">
                    Inspection Type
                  </label>
                  <select
                    value={inspectionType}
                    onChange={(e) => setInspectionType(e.target.value as InspectionType)}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent cursor-pointer"
                  >
                    <option value="construction">Construction Site</option>
                    <option value="bridge">Bridge / Structural</option>
                    <option value="tower">Water Tower / Vertical Asset</option>
                    <option value="utility">Utility / Linear Infrastructure</option>
                  </select>
                </div>

                {/* Dynamic Project Size Input */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">
                    Project Size
                  </label>
                  
                  {inspectionType === 'construction' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-stone-600">Project Value</span>
                        <span className="text-lg font-bold text-stone-900">
                          ${(constructionValue / 1000000).toFixed(1)}M
                        </span>
                      </div>
                      <input
                        type="range"
                        min="250000"
                        max="100000000"
                        step="250000"
                        value={constructionValue}
                        onChange={(e) => setConstructionValue(Number(e.target.value))}
                        className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                      />
                      <div className="flex justify-between text-xs text-stone-500 mt-1">
                        <span>$250K</span>
                        <span>$100M</span>
                      </div>
                    </div>
                  )}

                  {inspectionType === 'bridge' && (
                    <div className="grid grid-cols-3 gap-3">
                      {(['small', 'medium', 'large'] as BridgeSize[]).map((size) => (
                        <button
                          key={size}
                          onClick={() => setBridgeSize(size)}
                          className={`py-3 px-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                            bridgeSize === size
                              ? 'bg-amber-600 text-white'
                              : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                          }`}
                        >
                          {size.charAt(0).toUpperCase() + size.slice(1)}
                        </button>
                      ))}
                    </div>
                  )}

                  {inspectionType === 'tower' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-stone-600">Number of Structures</span>
                        <span className="text-lg font-bold text-stone-900">{towerCount}</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        step="1"
                        value={towerCount}
                        onChange={(e) => setTowerCount(Number(e.target.value))}
                        className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                      />
                      <div className="flex justify-between text-xs text-stone-500 mt-1">
                        <span>1</span>
                        <span>10</span>
                      </div>
                    </div>
                  )}

                  {inspectionType === 'utility' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-stone-600">Linear Miles</span>
                        <span className="text-lg font-bold text-stone-900">{utilityMiles}</span>
                      </div>
                      <input
                        type="range"
                        min="0.5"
                        max="100"
                        step="0.5"
                        value={utilityMiles}
                        onChange={(e) => setUtilityMiles(Number(e.target.value))}
                        className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                      />
                      <div className="flex justify-between text-xs text-stone-500 mt-1">
                        <span>0.5</span>
                        <span>100</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Inspection Depth */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">
                    Inspection Depth
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'visual', label: 'Visual Only', desc: 'High-resolution imagery' },
                      { value: 'thermal', label: 'Visual + Thermal', desc: '+30% - Includes thermal imaging' },
                      { value: 'diagnostic', label: 'Full Diagnostic Package', desc: '+60% - Complete analysis & reporting' }
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          inspectionDepth === option.value
                            ? 'border-amber-600 bg-amber-50'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="depth"
                          value={option.value}
                          checked={inspectionDepth === option.value}
                          onChange={(e) => setInspectionDepth(e.target.value as InspectionDepth)}
                          className="mt-1 cursor-pointer"
                        />
                        <div className="ml-3">
                          <div className="font-semibold text-stone-900">{option.label}</div>
                          <div className="text-sm text-stone-600">{option.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Add-ons */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-stone-700 mb-3">
                    Add-ons
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center p-4 border border-stone-200 rounded-lg hover:bg-stone-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={priorityTurnaround}
                        onChange={(e) => setPriorityTurnaround(e.target.checked)}
                        className="w-5 h-5 text-amber-600 cursor-pointer"
                      />
                      <div className="ml-3">
                        <div className="font-semibold text-stone-900">Priority Turnaround (48 hours)</div>
                        <div className="text-sm text-stone-600">+15%</div>
                      </div>
                    </label>
                    <label className="flex items-center p-4 border border-stone-200 rounded-lg hover:bg-stone-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={engineerReport}
                        onChange={(e) => setEngineerReport(e.target.checked)}
                        className="w-5 h-5 text-amber-600 cursor-pointer"
                      />
                      <div className="ml-3">
                        <div className="font-semibold text-stone-900">Engineer-Ready Summary Report</div>
                        <div className="text-sm text-stone-600">+20%</div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Results Panel */}
              <div className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl shadow-lg p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Estimated Price</h3>

                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center pb-3 border-b border-amber-500/30">
                    <span className="text-amber-100">Inspection Type</span>
                    <span className="font-semibold">{getInspectionTypeLabel()}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-amber-500/30">
                    <span className="text-amber-100">Project Size</span>
                    <span className="font-semibold">{result.projectSize}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-amber-500/30">
                    <span className="text-amber-100">Inspection Depth</span>
                    <span className="font-semibold">{getInspectionDepthLabel()}</span>
                  </div>
                  {parseFloat(result.multiplier) > 1.0 && (
                    <div className="flex justify-between items-center pb-3 border-b border-amber-500/30">
                      <span className="text-amber-100">Small Project Adjustment</span>
                      <span className="font-semibold">{result.multiplier}x</span>
                    </div>
                  )}
                  {(priorityTurnaround || engineerReport) && (
                    <div className="pb-3 border-b border-amber-500/30">
                      <div className="text-amber-100 mb-2">Add-ons:</div>
                      <div className="space-y-1 text-sm">
                        {priorityTurnaround && <div>• Priority Turnaround (+15%)</div>}
                        {engineerReport && <div>• Engineer Report (+20%)</div>}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white/10 rounded-lg p-6 mb-6">
                  <div className="text-amber-100 text-sm mb-2">Estimated Total</div>
                  <div className="text-4xl font-bold mb-2">
                    ${result.lowPrice.toLocaleString()} – ${result.highPrice.toLocaleString()}
                  </div>
                  <div className="text-sm text-amber-100">
                    Final pricing depends on access, environment, and reporting requirements.
                  </div>
                </div>

                <div className="space-y-3">
                  <button className="w-full bg-white text-amber-700 py-4 rounded-lg font-semibold hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer">
                    Get a Firm Quote
                  </button>
                  <button className="w-full bg-amber-800 text-white py-4 rounded-lg font-semibold hover:bg-amber-900 transition-colors whitespace-nowrap cursor-pointer">
                    Contact an Inspector
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-stone-900 mb-12 text-center">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-stone-900 mb-3">
                  Why do small projects cost more per unit?
                </h3>
                <p className="text-stone-700 leading-relaxed">
                  Mobilization, setup, and reporting have fixed costs regardless of project size. Small-project pricing reflects those costs fairly while keeping large projects efficient. As project size increases, per-unit costs decrease.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-stone-900 mb-3">
                  Can drones replace hands-on inspections?
                </h3>
                <p className="text-stone-700 leading-relaxed">
                  Drones provide inspection-grade documentation and identify issues that require closer review. They reduce the need for scaffolding, rope access, or lane closures, but engineering judgment remains essential for final assessments.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-stone-900 mb-3">
                  Do inspections require shutdowns?
                </h3>
                <p className="text-stone-700 leading-relaxed">
                  Most drone inspections do not require shutdowns, lane closures, or operational interruptions. We coordinate with site managers to ensure safe operations while maintaining normal activity.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-stone-900 mb-3">
                  What deliverables are included?
                </h3>
                <p className="text-stone-700 leading-relaxed">
                  Visual inspections include high-resolution imagery and annotated findings. Thermal inspections add infrared analysis. Full diagnostic packages include comprehensive reports with issue tagging, measurements, and engineer-ready documentation.
                </p>
              </div>

              <div className="bg-stone-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-stone-900 mb-3">
                  How fast is turnaround?
                </h3>
                <p className="text-stone-700 leading-relaxed">
                  Standard turnaround is 5–7 business days. Priority turnaround (48 hours) is available for time-sensitive projects. Turnaround begins after field work is complete and weather-dependent delays are resolved.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
